import { useTranslation } from "react-i18next";
import { Panel, PanelHeaderTemplateOptions } from 'primereact/panel';
import { Chip } from "primereact/chip";
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import NadaConsta from "../../../components/nadaConsta";
import { Score } from "../../../components/Score";
import { BarScore } from "../../../components/BarScore";
import CardResumo from "./CardResumo";
import { formatCpfOrCnpj, formatValueReais } from "../../../utils/ValidationUtils";

// Datas do fornecedor vêm como yyyy-MM-dd; formata sem new Date() para não deslocar o dia por fuso
const formatarData = (valor: any) => {
  if (!valor || typeof valor !== 'string') return '';
  const [ano, mes, dia] = valor.substring(0, 10).split('-');
  return dia && mes && ano ? `${dia}/${mes}/${ano}` : valor;
};

// Mês do histórico de consultas vem como yyyy-MM
const formatarMes = (valor: any) => {
  if (!valor || typeof valor !== 'string') return '';
  const [ano, mes] = valor.split('-');
  return ano && mes ? `${mes}/${ano}` : valor;
};

// defaultRate vem com 2 casas implícitas (ex.: "00622" = 6,22%)
const formatarTaxa = (valor: any) => {
  const numero = Number(valor);
  if (valor === undefined || valor === null || valor === '' || isNaN(numero)) return valor;
  return (numero / 100).toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
};

const formatarCep = (valor: any) => {
  const cep = String(valor || '').replace(/\D/g, '');
  return cep.length === 8 ? `${cep.substring(0, 5)}-${cep.substring(5)}` : valor;
};

const formatarTelefone = (telefone: any) => {
  if (!telefone) return '';
  if (typeof telefone !== 'object') return String(telefone);
  const ddd = telefone.areaCode ? `(${telefone.areaCode}) ` : '';
  return `${ddd}${telefone.phoneNumber || telefone.number || ''}`.trim();
};

// Localiza uma lista pelo nome da chave em qualquer nível do retorno (posição varia entre PF e PJ)
const buscarLista = (obj: any, chave: string): any[] => {
  if (!obj || typeof obj !== 'object') return [];
  if (Array.isArray(obj[chave])) return obj[chave];
  for (const valor of Object.values(obj)) {
    const encontrado = buscarLista(valor, chave);
    if (encontrado.length > 0) return encontrado;
  }
  return [];
};

// Subtítulos das seções no mesmo padrão do título dos painéis
const estiloSubtitulo = { fontFamily: 'var(--font-family)', fontSize: '1rem', fontWeight: 'bold', color: 'black', margin: '0 0 0.75rem 0' };

const simNao =(valor: any) => valor === undefined || valor === null ? '' : (valor ? 'Sim' : 'Não');

// O fornecedor não envia faixa: deriva pelas mesmas faixas exibidas no BarScore (0 a 1000)
const faixaScore = (valor: any) => {
  const numero = Number(valor);
  if (numero <= 200) return 'PESSIMO';
  if (numero <= 400) return 'RUIM';
  if (numero <= 600) return 'REGULAR';
  if (numero <= 800) return 'BOM';
  return 'OTIMO';
};

// Ordem definida pelo cliente: Cheques BACEN, Protesto Nacional, PEFIN, REFIN, Dívidas Vencidas
const CATEGORIAS_NEGATIVAS = [
  { chave: 'check', lista: 'checkResponse', titulo: 'Cheques sem Fundos - BACEN' },
  { chave: 'notary', lista: 'notaryResponse', titulo: 'Protesto Nacional' },
  { chave: 'pefin', lista: 'pefinResponse', titulo: 'Pendências Financeiras (PEFIN)' },
  { chave: 'refin', lista: 'refinResponse', titulo: 'Restrições Financeiras (REFIN)' },
  { chave: 'collectionRecords', lista: 'collectionRecordsResponse', titulo: 'Dívidas Vencidas' },
];

function CampoChip({ label, valor }: { label: string, valor: any }) {
  const { t } = useTranslation();
  return (
    <div className="col p-fluid flex flex-wrap">
      <div style={{ width: "100%" }}>
        <label className="label-dados">{t(label)}</label>
        <Chip style={{ width: "100%" }} label={valor !== undefined && valor !== null && valor !== '' ? String(valor) : 'Não Informado'} />
      </div>
    </div>
  );
}

export default function RelatorioBasico({
  data,
  titulo,
  pj
}: {
  data: any,
  titulo: string,
  pj?: boolean
}) {
  const { t } = useTranslation();

  // Na variação Custom o fornecedor devolve sem cadastro e score: o tipo vem do chamador
  const possuiCadastro = !!data?.registration;
  const registration = data?.registration || {};
  const endereco = registration.address || {};
  const negativeData = data?.negativeData || {};
  const score = data?.score;
  const isPJ = pj ?? !!registration.companyDocument;

  const resumos = CATEGORIAS_NEGATIVAS.map(categoria => {
    const bloco = negativeData[categoria.chave] || {};
    return {
      ...categoria,
      itens: bloco[categoria.lista] || [],
      quantidade: bloco.summary?.count || 0,
      valor: bloco.summary?.balance || 0,
      primeira: bloco.summary?.firstOccurrence,
      ultima: bloco.summary?.lastOccurrence,
    };
  });
  const possuiNegativos = resumos.some(r => r.quantidade > 0);

  // PF vem em facts.inquiry e PJ em facts.inquiryCompanyResponse
  const blocoConsultas = data?.facts?.inquiry || data?.facts?.inquiryCompanyResponse || {};
  const consultas = blocoConsultas.inquiryResponse || blocoConsultas.results || [];
  const quantidadeConsultas = blocoConsultas.quantity;
  const historicoConsultas = quantidadeConsultas?.historical || [];
  const socios = data?.partner?.PartnerResponse?.results || [];
  const administradores = data?.director?.DirectorResponse?.results || [];
  const participacoes = buscarLista(data, 'partnershipResponse');
  const telefones = Array.isArray(registration.phone) ? registration.phone : (registration.phone ? [registration.phone] : []);

  const headerTemplate = (options: PanelHeaderTemplateOptions) => {
    const titleStyle = { marginLeft: '1rem', fontFamily: 'var(--font-family)', fontSize: '1rem', color: 'black' };
    return (
      <div className={`${options.className} `}>
        {possuiNegativos ? (
          <span style={{ color: '#C70039', fontSize: '1.5rem' }} className="pi pi-exclamation-triangle">
            <span className="p-panel-title" style={titleStyle}>{t(titulo)}</span>
          </span>
        ) : (
          <span className="p-panel-title" style={titleStyle}>{t(titulo)}</span>
        )}
      </div>
    );
  };

  const colunaDataInclusao = <Column key="inclusionDate" field="inclusionDate" header={t('Data Inclusão')} body={(r) => formatarData(r.inclusionDate)} />;
  const colunaCodigo = <Column key="cadus" field="cadus" header={t('Código')} />;

  const colunasPorCategoria: Record<string, JSX.Element[]> = {
    notary: [
      <Column key="occurrenceDate" field="occurrenceDate" header={t('Data Ocorrência')} body={(r) => formatarData(r.occurrenceDate)} />,
      colunaDataInclusao,
      <Column key="amount" field="amount" header={t('Valor')} body={(r) => formatValueReais(r.amount)} />,
      <Column key="officeNumber" field="officeNumber" header={t('Cartório')} body={(r) => r.officeName || r.officeNumber} />,
      <Column key="city" field="city" header={t('Cidade')} />,
      <Column key="federalUnit" field="federalUnit" header={t('UF')} />,
      colunaCodigo,
    ],
    check: [
      <Column key="occurrenceDate" field="occurrenceDate" header={t('Data Ocorrência')} body={(r) => formatarData(r.occurrenceDate)} />,
      colunaDataInclusao,
      <Column key="bankName" field="bankName" header={t('Banco')} body={(r) => r.bankName || r.bankId} />,
      <Column key="bankAgencyId" field="bankAgencyId" header={t('Agência')} />,
      <Column key="checkNumber" field="checkNumber" header={t('Nº Cheque')} />,
      <Column key="checkCount" field="checkCount" header={t('Qtd. Cheques')} />,
      <Column key="alinea" field="alinea" header={t('Alínea')} />,
      <Column key="city" field="city" header={t('Cidade')} />,
      <Column key="federalUnit" field="federalUnit" header={t('UF')} />,
      colunaCodigo,
    ],
  };
  const colunasPadrao = [
    <Column key="occurrenceDate" field="occurrenceDate" header={t('Data Ocorrência')} body={(r) => formatarData(r.occurrenceDate)} />,
    colunaDataInclusao,
    <Column key="legalNature" field="legalNature" header={t('Natureza')} body={(r) => [r.legalNatureId, r.legalNature].filter(Boolean).join(' - ')} />,
    <Column key="creditorName" field="creditorName" header={t('Credor')} body={(r) => r.creditorName || 'Não Informado'} />,
    <Column key="contractId" field="contractId" header={t('Contrato')} />,
    <Column key="amount" field="amount" header={t('Valor')} body={(r) => formatValueReais(r.amount)} />,
    <Column key="city" field="city" header={t('Cidade')} />,
    <Column key="federalUnit" field="federalUnit" header={t('UF')} />,
    // principal = devedor principal; quando false, o consultado figura como avalista
    <Column key="principal" field="principal" header={t('Avalista')} body={(r) => r.principal === undefined || r.principal === null ? '' : simNao(!r.principal)} />,
    <Column key="dispute" field="dispute" header={t('Em Contestação')} body={(r) => simNao(r.dispute?.disputeIndicativeFlag)} />,
    colunaCodigo,
  ];

  return (
    <Panel headerTemplate={headerTemplate}>
      {/* Mesmo sem retorno do fornecedor exibe todas as seções com Nada Consta */}
      {(
        <div>
          {possuiCadastro && (
            <>
              <div className="grid">
                <CampoChip label={isPJ ? 'Razão Social' : 'Nome'} valor={registration.companyName || registration.consumerName} />
                <CampoChip label="documento" valor={formatCpfOrCnpj(registration.companyDocument || registration.documentNumber || '')} />
              </div>
              <div className="grid">
                {isPJ ? (
                  <CampoChip label="Data de Fundação" valor={formatarData(registration.foundationDate)} />
                ) : (
                  <>
                    <CampoChip label="Data de Nascimento" valor={formatarData(registration.birthDate)} />
                    <CampoChip label="Nome da Mãe" valor={registration.motherName} />
                  </>
                )}
                <CampoChip label="Situação Cadastral" valor={registration.statusRegistration} />
                {registration.statusDate && <CampoChip label="Data da Situação" valor={formatarData(registration.statusDate)} />}
              </div>
            </>
          )}
          {registration.address && (
            <div className="grid">
              <CampoChip label="Endereço" valor={endereco.addressLine} />
              <CampoChip label="Bairro" valor={endereco.district} />
              <CampoChip label="Cidade" valor={endereco.city} />
              <CampoChip label="UF" valor={endereco.state} />
              <CampoChip label="CEP" valor={formatarCep(endereco.zipCode)} />
            </div>
          )}
          {telefones.length > 0 && (
            <div className="grid">
              {telefones.map((telefone: any, i: number) => (
                <CampoChip key={i} label="Telefone" valor={formatarTelefone(telefone)} />
              ))}
            </div>
          )}

          {score && (
            <>
              <br></br>
              <div style={{ display: "flex", gap: "40px" }}>
                <div className="scrScore">
                  <Score score={String(score.score ?? 0)} range={faixaScore(score.score)} />
                </div>
                <BarScore score={String(score.score ?? 0)} rangeColor={faixaScore(score.score)} />
              </div>
              <div className="grid">
                {score.range && <CampoChip label="Faixa" valor={score.range} />}
                {score.defaultRate && <CampoChip label="Probabilidade de Inadimplência (%)" valor={formatarTaxa(score.defaultRate)} />}
                {score.scoreModel && <CampoChip label="Modelo do Score" valor={score.scoreModel} />}
              </div>
              {score.message && (
                <div className="grid">
                  <CampoChip label="Mensagem Score" valor={score.message} />
                </div>
              )}
            </>
          )}

          <br></br>
          <div className="flex-consulta flex-wrap gap-3">
            {resumos.map(r => (
              <CardResumo key={r.chave} titulo={r.titulo} quantidade={r.quantidade} valor={r.valor} primeira={formatarData(r.primeira)} ultima={formatarData(r.ultima)} />
            ))}
          </div>

          {resumos.map(r => (
            <div key={r.chave}>
              <br></br>
              <h4 style={estiloSubtitulo}>{t(r.titulo)}{r.quantidade > r.itens.length ? ` (${t('exibindo')} ${r.itens.length} ${t('de')} ${r.quantidade})` : ''}</h4>
              {r.itens.length > 0 ? (
                <DataTable size="small" value={r.itens} tableStyle={{ minWidth: '50rem' }}>
                  {colunasPorCategoria[r.chave] || colunasPadrao}
                </DataTable>
              ) : r.quantidade === 0 && <NadaConsta />}
            </div>
          ))}

          {socios.length > 0 && (
            <>
              <br></br>
              <h4 style={estiloSubtitulo}>{t('Sócios')}</h4>
              <DataTable size="small" value={socios} tableStyle={{ minWidth: '50rem' }}>
                <Column field="documentId" header={t('documento')} body={(r) => formatCpfOrCnpj(r.documentId || '')} />
                <Column field="name" header={t('Nome')} body={(r) => r.name || 'Não Informado'} />
                <Column field="participationPercentage" header={t('Participação (%)')} body={(r) => r.participationPercentage !== undefined ? Number(r.participationPercentage).toFixed(2) : ''} />
                <Column field="status" header={t('Situação')} />
                <Column field="hasNegative" header={t('Possui Restrição')} body={(r) => simNao(r.hasNegative)} />
                <Column field="inconsistent" header={t('Inconsistente')} body={(r) => simNao(r.inconsistent)} />
              </DataTable>
            </>
          )}

          {administradores.length > 0 && (
            <>
              <br></br>
              <h4 style={estiloSubtitulo}>{t('Administradores')}</h4>
              <DataTable size="small" value={administradores} tableStyle={{ minWidth: '50rem' }}>
                <Column field="documentId" header={t('documento')} body={(r) => `${r.documentType ? r.documentType + ' ' : ''}${formatCpfOrCnpj(r.documentId || '')}`} />
                <Column field="name" header={t('Nome')} body={(r) => r.name || 'Não Informado'} />
                <Column field="role" header={t('Cargo')} />
                <Column field="status" header={t('Situação')} />
                <Column field="hasNegative" header={t('Possui Restrição')} body={(r) => simNao(r.hasNegative)} />
              </DataTable>
            </>
          )}

          {participacoes.length > 0 && (
            <>
              <br></br>
              <h4 style={estiloSubtitulo}>{t('Participações em Empresas')}</h4>
              <DataTable size="small" value={participacoes} tableStyle={{ minWidth: '50rem' }}>
                <Column field="businessDocument" header={t('CNPJ')} body={(r) => formatCpfOrCnpj(r.businessDocument || '')} />
                <Column field="companyName" header={t('Razão Social')} body={(r) => r.companyName || 'Não Informado'} />
                <Column field="participationPercentage" header={t('Participação (%)')} body={(r) => r.participationPercentage !== undefined ? Number(r.participationPercentage).toFixed(2) : ''} />
                <Column field="companyStatus" header={t('Situação')} />
                <Column field="companyStatusDate" header={t('Data da Situação')} body={(r) => formatarData(r.companyStatusDate)} />
                <Column field="companyState" header={t('UF')} />
                <Column field="hasNegative" header={t('Possui Restrição')} body={(r) => simNao(r.hasNegative)} />
                <Column field="updateDate" header={t('Atualizado em')} body={(r) => formatarData(r.updateDate)} />
              </DataTable>
            </>
          )}

          <br></br>
          <h4 style={estiloSubtitulo}>{t('Últimas Consultas Realizadas')}</h4>
          {!(consultas.length > 0 || quantidadeConsultas) && <NadaConsta />}
          {(consultas.length > 0 || quantidadeConsultas) && (
            <>
              {quantidadeConsultas && (
                <div className="grid">
                  <CampoChip label="Consultas no Mês Atual" valor={quantidadeConsultas.actual ?? 0} />
                  <CampoChip label="Consultas Bancárias no Mês Atual" valor={quantidadeConsultas.bankActual ?? 0} />
                </div>
              )}
              {historicoConsultas.length > 0 && (
                <DataTable size="small" value={historicoConsultas} tableStyle={{ minWidth: '30rem' }}>
                  <Column field="inquiryDate" header={t('Mês')} body={(r) => formatarMes(r.inquiryDate)} />
                  <Column field="occurrences" header={t('Consultas')} />
                  <Column field="bankOccurrences" header={t('Consultas Bancárias')} />
                </DataTable>
              )}
              {consultas.length > 0 && (
                <>
                  <br></br>
                  <DataTable size="small" value={consultas} tableStyle={{ minWidth: '50rem' }}>
                    <Column field="occurrenceDate" header={t('data')} body={(r) => formatarData(r.occurrenceDate)} />
                    {isPJ && <Column field="companyName" header={t('Consultante')} body={(r) => r.companyName || 'Não Informado'} />}
                    {isPJ && <Column field="companyAlias" header={t('Nome Fantasia')} />}
                    {isPJ && <Column field="companyDocumentId" header={t('documento')} body={(r) => formatCpfOrCnpj(r.companyDocumentId || '')} />}
                    {!isPJ && <Column field="segmentDescription" header={t('Segmento')} />}
                    <Column field="daysQuantity" header={t('Qtd. Consultas')} />
                  </DataTable>
                </>
              )}
            </>
          )}
        </div>
      )}
    </Panel>
  );
}
